import { Paperclip, Pencil, SendHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { IconButton } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { formatBytes } from '@/utils/format'
import { uploadAttachment, useEditMessage, useSendMessage } from '../api'
import type { ChatMessage } from '../types'

const MAX_BYTES = 25 * 1024 * 1024
const MAX_FILES = 10

interface Pending {
  key: string
  file: File
  progress: number
  id?: number
  error?: string
}

interface ComposerProps {
  conversationId: number
  editing: ChatMessage | null
  onCancelEdit: () => void
  onSent: () => void
}

/** Write, attach and send (Enter sends, Shift+Enter for a new line). Also edits a message. */
export function Composer({ conversationId, editing, onCancelEdit, onSent }: ComposerProps) {
  const { t } = useTranslation('messaging')
  const [text, setText] = useState('')
  const [files, setFiles] = useState<Pending[]>([])
  const area = useRef<HTMLTextAreaElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  const send = useSendMessage(conversationId)
  const edit = useEditMessage()

  // Starting (or stopping) an edit replaces the text being written.
  const [editingNow, setEditingNow] = useState(editing)
  if (editing !== editingNow) {
    setEditingNow(editing)
    setText(editing?.body ?? '')
  }
  useEffect(() => {
    if (editing) area.current?.focus()
  }, [editing])

  // Grow with the text, up to about eight lines.
  useEffect(() => {
    const el = area.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])

  const uploading = files.some((f) => f.id === undefined && !f.error)
  const ready = files.filter((f) => f.id !== undefined)
  const canSend =
    !send.isPending && !edit.isPending && !uploading && (text.trim() !== '' || (!editing && ready.length > 0))

  const addFiles = (list: FileList | null) => {
    if (!list) return
    const incoming = Array.from(list).slice(0, MAX_FILES - files.length)
    for (const file of incoming) {
      const key = `${file.name}-${file.size}-${Math.random()}`
      if (file.size > MAX_BYTES) {
        toast.error(t('{{name}} is too large', { name: file.name }), t('Files may be up to 25 MB.'))
        continue
      }
      setFiles((f) => [...f, { key, file, progress: 0 }])
      uploadAttachment(file, (progress) =>
        setFiles((f) => f.map((p) => (p.key === key ? { ...p, progress } : p))),
      )
        .then((stored) =>
          setFiles((f) => f.map((p) => (p.key === key ? { ...p, id: stored.id, progress: 100 } : p))),
        )
        .catch((e: unknown) => {
          const message = toApiError(e).message
          toast.error(t('{{name}} could not be attached', { name: file.name }), message)
          setFiles((f) => f.filter((p) => p.key !== key))
        })
    }
  }

  const submit = async () => {
    if (!canSend) return
    try {
      if (editing) {
        await edit.mutateAsync({ id: editing.id, body: text.trim() })
        onCancelEdit()
      } else {
        await send.mutateAsync({
          body: text.trim() || undefined,
          fileIds: ready.flatMap((f) => (f.id === undefined ? [] : [f.id])),
        })
        setFiles([])
        onSent()
      }
      setText('')
    } catch (e) {
      toast.error(t('The message was not sent'), toApiError(e).message)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    } else if (e.key === 'Escape' && editing) {
      onCancelEdit()
    }
  }

  return (
    <div
      className="border-t border-line bg-surface p-3"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        if (!editing) addFiles(e.dataTransfer.files)
      }}
    >
      {editing ? (
        <div className="mb-2 flex items-center gap-2 rounded-control bg-brand-50 px-3 py-1.5 text-xs text-brand-800">
          <Pencil className="size-3.5" aria-hidden="true" />
          <span className="flex-1">{t('Editing a message')}</span>
          <button type="button" onClick={onCancelEdit} className="font-medium hover:underline">
            {t('Cancel')}
          </button>
        </div>
      ) : null}
      {files.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label={t('Attachments')}>
          {files.map((f) => (
            <li
              key={f.key}
              className="flex items-center gap-2 rounded-full border border-line bg-surface-muted py-1 pr-1 pl-3 text-xs"
            >
              <span className="max-w-40 truncate">{f.file.name}</span>
              <span className="text-subtle">
                {f.id !== undefined ? formatBytes(f.file.size) : `${f.progress} %`}
              </span>
              {f.id === undefined ? <Spinner className="size-3.5" /> : null}
              <button
                type="button"
                onClick={() => setFiles((all) => all.filter((p) => p.key !== f.key))}
                className="grid size-5 place-items-center rounded-full hover:bg-surface-sunken"
                aria-label={t('Remove {{name}}', { name: f.file.name })}
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2">
        {!editing ? (
          <>
            <IconButton
              label={t('Attach files')}
              icon={<Paperclip />}
              variant="ghost"
              onClick={() => picker.current?.click()}
              disabled={files.length >= MAX_FILES}
            />
            <input
              ref={picker}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files)
                e.target.value = ''
              }}
            />
          </>
        ) : null}
        <label className="flex-1">
          <span className="sr-only">{t('Message')}</span>
          <textarea
            ref={area}
            rows={1}
            value={text}
            maxLength={4000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={(e) => {
              if (!editing && e.clipboardData.files.length > 0) addFiles(e.clipboardData.files)
            }}
            placeholder={t('Write a message…')}
            className="block max-h-[200px] w-full resize-none rounded-control border border-line bg-surface-muted px-3 py-2 text-sm text-ink placeholder:text-subtle focus:border-brand-400 focus:bg-surface focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
        </label>
        <IconButton
          label={editing ? t('Save') : t('Send')}
          icon={<SendHorizontal />}
          variant="primary"
          onClick={() => void submit()}
          disabled={!canSend}
          loading={send.isPending || edit.isPending}
        />
      </div>
    </div>
  )
}
